import { useCallback, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import { fastFileHash } from 'helpers/hash/fastFileHash'
import { type Orientation, validateMedia } from 'helpers/media'
import {
  abortVideoMultipart,
  attachPhotoFromS3,
  attachVideoFromS3,
  checkPhotoDupe,
  checkVideoDupe,
  completeVideoMultipart,
  presignPhoto,
  startVideoMultipart,
  uploadFileToS3,
  uploadVideoMultipart,
} from 'services/endpoints/attachment'
import { safeRandomUUID } from 'utils/safeRandomUUID'

import { type ReelVideos } from '../useCreateReel/useCreateReel'

interface UploadReelMediaProps {
  setImageData: (
    path: string,
    orientation: Orientation,
    exif_data: any,
    storage_path?: string,
    thumb_path?: string | null,
    id?: string
  ) => void
  setVideoData: (videos: ReelVideos) => void
  setProgressInfo: ((pct: number, cancelCb: () => void) => void) & ((reset: null) => void)
  clearMedia: () => void
  setMediaType: (mediaType: File | null) => void
  setThumbnail?: (thumbnail: string) => void
  onTooManyFiles?: (count: number) => void
}

const CANCELLED_CODE = '__UPLOAD_CANCELLED__'

export function useUploadReelMedia({
  setImageData,
  setVideoData,
  setProgressInfo,
  clearMedia,
  setMediaType,
  setThumbnail,
  onTooManyFiles,
}: UploadReelMediaProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const uploadCancelledRef = useRef(false)
  const activeCancelRef = useRef<(() => void) | null>(null)
  const { t } = useTranslation()

  const handleMediaUploadClick = useCallback(() => {
    fileRef.current?.click()
  }, [])

  const cancelCurrentUpload = useCallback(() => {
    uploadCancelledRef.current = true
    try {
      activeCancelRef.current?.()
    } catch {}
    setProgressInfo(null as any)
  }, [setProgressInfo])

  const throwIfCancelled = useCallback(() => {
    if (!uploadCancelledRef.current) return

    const error = new Error(CANCELLED_CODE)
    ;(error as any).code = CANCELLED_CODE
    throw error
  }, [])

  const uploadFile = useCallback(
    async (file: File) => {
      uploadCancelledRef.current = false
      activeCancelRef.current = null

      clearMedia()
      setProgressInfo(null as any)

      try {
        // -------------------- IMAGE --------------------
        if (file.type.startsWith('image/')) {
          await validateMedia(file, t)
          throwIfCancelled()

          setProgressInfo(0 as any, cancelCurrentUpload)

          const localUrl = URL.createObjectURL(file)
          const imageMeta = await getImageMeta(file)
          throwIfCancelled()

          const orientation: Orientation =
            imageMeta.orientation === 'landscape' ? 'Landscape' : 'Portrait'

          const baseExif = {
            width: imageMeta.width,
            height: imageMeta.height,
          }

          setImageData(localUrl, orientation, baseExif)
          setMediaType(file)

          const fileHash = await fastFileHash(file)
          throwIfCancelled()

          const dupe = await checkPhotoDupe({
            h: fileHash,
            size: file.size,
            group_ids: [],
          })

          throwIfCancelled()

          if (dupe?.status === 'OK') {
            const data: any = dupe.data ?? {}

            setImageData(
              localUrl,
              orientation,
              { ...baseExif },
              data?.storage_path || data?.path || '',
              data?.thumb_path || null,
              data.id
            )

            setProgressInfo(100 as any)
            return
          }

          const ext = (file.name.split('.').pop() ?? 'jpg').toLowerCase()
          const s3Key = `uploads/users/reels/images/originals/${safeRandomUUID()}.${ext}`

          const { url: putUrl, key } = await presignPhoto({
            type: 'post_image',
            contentType: file.type,
            key: s3Key,
            size: file.size,
          })

          throwIfCancelled()

          await uploadFileToS3(
            putUrl,
            file,
            (loaded: number, total: any, cancelCb: () => void) => {
              activeCancelRef.current = cancelCb

              if (uploadCancelledRef.current) {
                try {
                  cancelCb?.()
                } catch {}
                return
              }

              const pct = Math.round((loaded * 100) / (total || file.size || 1))

              setProgressInfo(pct, () => {
                uploadCancelledRef.current = true
                try {
                  cancelCb?.()
                } catch {}
                setProgressInfo(null as any)
              })
            }
          )

          throwIfCancelled()

          const attach = await attachPhotoFromS3({
            s3_key: key,
            type: 'post',
            hash_image: fileHash,
          })

          throwIfCancelled()

          const payload: any = attach?.data ?? attach
          const data: any = payload?.data ?? payload

          setImageData(
            localUrl,
            orientation,
            { ...baseExif },
            data?.storage_path || key,
            data?.thumb_path || null,
            data.id
          )

          setProgressInfo(100 as any)
          return
        }

        // -------------------- VIDEO --------------------
        if (file.type.startsWith('video/')) {
          await validateMedia(file, t)
          throwIfCancelled()

          setProgressInfo(0 as any, cancelCurrentUpload)

          const localUrl = URL.createObjectURL(file)
          const meta = await getVideoMeta(localUrl)
          throwIfCancelled()

          const orientation: Orientation =
            meta.orientation === 'landscape' ? 'Landscape' : 'Portrait'

          const safeDuration = Math.max(
            0,
            Math.round((meta.duration || 0) * 100) / 100
          )

          if (setThumbnail) {
            try {
              const thumb = await extractVideoThumbnail(localUrl)
              throwIfCancelled()
              if (thumb) setThumbnail(thumb)
            } catch (error: any) {
              if (
                error?.code === CANCELLED_CODE ||
                error?.message === CANCELLED_CODE
              ) {
                throw error
              }
            }
          }

          throwIfCancelled()

          setVideoData([
            {
              s3_path: localUrl,
              video_exif: {
                width: meta.width,
                height: meta.height,
              },
              orientation,
              isLocal: true,
            } as any,
          ])

          setMediaType(file)

          const fileHash = await fastFileHash(file)
          throwIfCancelled()

          const dupe = await checkVideoDupe({
            h: fileHash,
            size: file.size,
            group_ids: [],
          })

          throwIfCancelled()

          if (dupe?.status === 'OK') {
            const data: any = dupe.data ?? {}

            const bePath: string =
              data.video_src ||
              data.path ||
              data.storage_path ||
              ''

            setVideoData([
              {
                id: data.id,
                path: bePath,
                s3_path: bePath,
                video_exif: JSON.stringify({
                  width: meta.width,
                  height: meta.height,
                }),
                orientation:
                  data.original_orientation ??
                  data.orientation ??
                  orientation,
                isLocal: false,
                trim_start: null,
                trim_end: null,
              } as any,
            ])

            setProgressInfo(100 as any)
            return
          }

          const controller = new AbortController()
          let multipartKey = ''
          let multipartUploadId = ''

          const cancelMultipart = () => {
            uploadCancelledRef.current = true
            controller.abort()
            setProgressInfo(null as any)
          }

          activeCancelRef.current = cancelMultipart

          try {
            const multipart = await startVideoMultipart(
              {
                type: 'post_video',
                contentType: file.type,
                size: file.size,
              },
              controller.signal
            )

            multipartKey = multipart.key
            multipartUploadId = multipart.uploadId

            throwIfCancelled()

            const completedParts = await uploadVideoMultipart({
              file,
              key: multipart.key,
              uploadId: multipart.uploadId,
              partSize: multipart.partSize,
              totalParts: multipart.totalParts,
              parts: multipart.parts,
              contentType: multipart.contentType,
              signal: controller.signal,
              setProgress: (loaded, total) => {
                if (uploadCancelledRef.current) {
                  controller.abort()
                  return
                }

                const pct = Math.round(
                  (loaded * 100) / (total || file.size || 1)
                )

                setProgressInfo(pct, cancelMultipart)
              },
            })

            throwIfCancelled()

            const completed = await completeVideoMultipart(
              {
                key: multipart.key,
                uploadId: multipart.uploadId,
                parts: completedParts,
              },
              controller.signal
            )

            throwIfCancelled()

            const key = completed.key ?? multipart.key

            const attach = await attachVideoFromS3(
              {
                s3_key: key,
                duration:
                  safeDuration > 0
                    ? Number(safeDuration.toFixed(2))
                    : null,
                orientation,
                video_hash: fileHash,
              },
              controller.signal
            )

            throwIfCancelled()

            const payload: any = attach?.data ?? attach
            const data: any = payload?.data ?? payload

            if (!data?.id) {
              throw new Error(
                'Video attachment response did not contain an id'
              )
            }

            const bePath: string =
              data.video_src ||
              data.path ||
              data.storage_path ||
              key

            setVideoData([
              {
                id: data.id,
                path: bePath,
                s3_path: bePath,
                video_exif: JSON.stringify({
                  width: meta.width,
                  height: meta.height,
                  duration: safeDuration,
                }),
                orientation:
                  data.original_orientation ??
                  data.orientation ??
                  orientation,
                isLocal: false,
              },
            ])

            setProgressInfo(100 as any)
          } catch (error: any) {
            if (multipartKey && multipartUploadId) {
              await abortVideoMultipart({
                key: multipartKey,
                uploadId: multipartUploadId,
              })
            }

            throw error
          }

          return
        }
      } catch (error: any) {
        if (
          error?.code === CANCELLED_CODE ||
          error?.message === CANCELLED_CODE ||
          error?.name === 'AbortError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return
        }

        throw error
      }
    },
    [
      clearMedia,
      setProgressInfo,
      cancelCurrentUpload,
      throwIfCancelled,
      setThumbnail,
      t,
      setVideoData,
      setMediaType,
      setImageData,
    ]
  )

  const handleSetUpload = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      const input = event.target as HTMLInputElement
      const files = input.files

      if (!files || files.length === 0) {
        setMediaType(null)
        return
      }

      if (files.length > 1) {
        onTooManyFiles?.(files.length)

        const first = files[0]
        setMediaType(first)
        void uploadFile(first)

        input.value = ''
        return
      }

      const file = files[0]

      setMediaType(file)
      void uploadFile(file)

      input.value = ''
    },
    [onTooManyFiles, setMediaType, uploadFile]
  )

  return {
    fileRef,
    handleMediaUploadClick,
    handleSetUpload,
  }
}

const getImageMeta = async (
  file: File
): Promise<{
  width: number
  height: number
  orientation: 'portrait' | 'landscape'
}> =>
  await new Promise(resolve => {
    const img = new Image()
    const reader = new FileReader()

    reader.onload = () => {
      if (typeof reader.result === 'string') img.src = reader.result
      else resolve({ width: 0, height: 0, orientation: 'landscape' })
    }

    img.onload = () => {
      const width = img.width || 0
      const height = img.height || 0

      resolve({
        width,
        height,
        orientation: width >= height ? 'landscape' : 'portrait',
      })
    }

    img.onerror = () =>
      resolve({
        width: 0,
        height: 0,
        orientation: 'landscape',
      })

    reader.onerror = () =>
      resolve({
        width: 0,
        height: 0,
        orientation: 'landscape',
      })

    reader.readAsDataURL(file)
  })

const getVideoMeta = async (
  url: string
): Promise<{
  width: number
  height: number
  duration: number
  orientation: 'portrait' | 'landscape'
}> =>
  await new Promise(resolve => {
    const video = document.createElement('video')

    video.preload = 'metadata'
    video.src = url
    video.muted = true
    video.playsInline = true

    const finalize = () => {
      const width = video.videoWidth || 0
      const height = video.videoHeight || 0
      const duration =
        Number.isFinite(video.duration) && video.duration > 0
          ? Math.round(video.duration * 100) / 100
          : 0

      resolve({
        width,
        height,
        duration,
        orientation: width >= height ? 'landscape' : 'portrait',
      })
    }

    let tries = 0
    const maxTries = 10

    const tick = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        return finalize()
      }

      if (++tries >= maxTries) return finalize()

      setTimeout(tick, 50)
    }

    video.onloadedmetadata = tick

    video.onerror = () =>
      resolve({
        width: 0,
        height: 0,
        duration: 0,
        orientation: 'landscape',
      })
  })

const extractVideoThumbnail = async (
  videoUrl: string
): Promise<string> =>
  await new Promise(resolve => {
    const video = document.createElement('video')

    video.src = videoUrl
    video.crossOrigin = 'anonymous'
    video.muted = true
    video.playsInline = true

    video.onloadedmetadata = () => {
      video.currentTime = Math.min(
        1,
        Math.max(0, (video.duration || 1) - 0.1)
      )
    }

    video.onseeked = () => {
      const canvas = document.createElement('canvas')

      canvas.width = video.videoWidth
      canvas.height = video.videoHeight

      const ctx = canvas.getContext('2d')
      if (!ctx) return resolve('')

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      resolve(canvas.toDataURL('image/png'))
    }

    video.onerror = () => resolve('')
  })